import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, ArrowRight, Receipt } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PurchaseSuccessPage() {
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [transaction, setTransaction] = useState(null);
  const navigate = useNavigate();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Reliability: guard against setState after unmount and against
    // React StrictMode double-mount triggering two verification passes.
    let cancelled = false;

    const verifyPurchase = async () => {
      // Give webhook time to finalize the transaction on the server.
      await new Promise((r) => setTimeout(r, 2000));
      if (cancelled) return;
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API}/api/buyer/purchased-leads`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.ok) {
          const leads = await res.json();
          const matchingLead = leads.find((l) =>
            l.transactions?.some((t) => t.stripe_session_id === sessionId),
          );
          if (matchingLead) {
            const txn = matchingLead.transactions.find(
              (t) => t.stripe_session_id === sessionId,
            );
            if (!cancelled) {
              setTransaction({ lead: matchingLead, ...txn });
              toast.success('Lead purchase successful!');
            }
          } else if (!cancelled) {
            toast.info('Purchase is processing. Please check your dashboard.');
          }
        }
      } catch {
        // Silent — user still sees the "Processing" fallback view.
      } finally {
        if (!cancelled) setVerifying(false);
      }
    };

    if (sessionId) {
      verifyPurchase();
    } else {
      setVerifying(false);
    }

    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-black flex items-center justify-center px-6">
      <div className="max-w-2xl w-full">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-12">
          {verifying ? (
            <div className="text-center">
              <Loader2 className="h-16 w-16 text-emerald-500 animate-spin mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-white mb-4">Finalizing Your Purchase</h1>
              <p className="text-zinc-400 text-lg">Please wait while we unlock your lead...</p>
            </div>
          ) : transaction ? (
            <>
              <div className="text-center mb-8">
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="h-12 w-12 text-white" />
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">Purchase Complete!</h1>
                <p className="text-zinc-300 text-lg">
                  Your lead has been unlocked and is ready to view
                </p>
              </div>
              
              {/* Transaction Details */}
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-2xl p-6 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <Receipt className="h-6 w-6 text-emerald-500" />
                  <h2 className="text-xl font-semibold text-white">Transaction Summary</h2>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-4 border-b border-zinc-700">
                    <div>
                      <div className="text-sm text-zinc-500 mb-1">Lead</div>
                      <div className="text-white font-medium">
                        {transaction.lead?.digital_title || 'Lead'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-zinc-500 mb-1">Price</div>
                      <div className="text-white font-semibold text-lg">
                        ${transaction.purchase_price || transaction.lead?.price}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="text-zinc-400">Transaction ID</div>
                    <div className="text-white font-mono text-sm">
                      {transaction.transaction_id?.substring(0, 12)}...
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="text-zinc-400">Status</div>
                    <div className="text-emerald-500 font-semibold">
                      {transaction.delivery_status || 'Delivered'}
                    </div>
                  </div>
                  
                  {transaction.exclusivity_status && (
                    <div className="flex justify-between items-center">
                      <div className="text-zinc-400">Exclusivity</div>
                      <div className="text-white capitalize">
                        {transaction.exclusivity_status}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate(`/leads/${transaction.lead?.lead_id}`)}
                  size="lg"
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold shadow-xl"
                  data-testid="view-lead-btn"
                >
                  View Lead Details
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                
                <Button
                  onClick={() => navigate('/buyer/dashboard')}
                  size="lg"
                  variant="outline"
                  className="border-zinc-700 text-white hover:bg-zinc-800"
                  data-testid="back-to-dashboard-btn"
                >
                  Back to Dashboard
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center">
              <div className="h-20 w-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="h-12 w-12 text-amber-500" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">Processing Purchase</h1>
              <p className="text-zinc-400 text-lg mb-8">
                Your payment is being processed. This usually takes a few moments.
              </p>
              
              <Button
                onClick={() => navigate('/buyer/dashboard')}
                size="lg"
                className="bg-white text-black hover:bg-zinc-200 font-semibold"
                data-testid="check-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
